import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from '../../../components/Auth/AuthLayout';
import LoadingButton from '../../../components/Auth/LoadingButton';

const AdminPaymentFailurePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const transactionId = searchParams.get('transaction_id') || 'TXN-ADMIN-FAILED-' + Date.now();
  const amount = searchParams.get('amount') || '99.00';
  const reason = searchParams.get('reason') || 'Payment processing failed';

  const errorIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
  );

  return (
    <AuthLayout
      title="Admin Payment Failed"
      subtitle="Your admin subscription payment could not be processed"
      icon={errorIcon}
      iconBgColor="bg-jet-red-100"
      iconColor="text-jet-red-600"
    >
      <div className="space-y-6">
        {/* Payment Failure Details */}
        <div className="bg-jet-red-50 border border-jet-red-200 rounded-md p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-jet-red-900 mb-4">Payment Processing Failed</h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-jet-red-700">Transaction ID:</span>
                <span className="text-jet-red-900 font-mono">{transactionId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-jet-red-700">Amount:</span>
                <span className="text-jet-red-900 font-semibold">${amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-jet-red-700">Reason:</span>
                <span className="text-jet-red-900">{reason}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-jet-red-700">Date:</span>
                <span className="text-jet-red-900">{new Date().toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Common Reasons */}
        <div className="bg-jet-yellow-50 border border-jet-yellow-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-jet-yellow-900 mb-2">Common Payment Issues:</h4>
          <ul className="text-sm text-jet-yellow-800 space-y-1">
            <li>• Insufficient funds in account</li>
            <li>• Card expired or blocked</li>
            <li>• Incorrect billing information</li>
            <li>• Bank security restrictions</li>
            <li>• Network connectivity issues</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          <LoadingButton
            onClick={() => navigate('/admin/billing/retry-payment', { 
              state: { transactionId, amount } 
            })}
            variant="primary"
            className="bg-jet-red-600 hover:bg-jet-red-700 text-white"
          >
            Retry Payment
          </LoadingButton>

          <LoadingButton
            onClick={() => navigate('/admin/billing/payment-methods')}
            variant="secondary"
          >
            Update Payment Method
          </LoadingButton>

          <LoadingButton
            onClick={() => navigate('/admin/billing/contact-support')}
            variant="secondary"
          >
            Contact Billing Support
          </LoadingButton>
        </div>

        {/* Support Information */}
        <div className="bg-jet-blue-50 border border-jet-blue-200 rounded-md p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-jet-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <h4 className="text-sm font-medium text-jet-blue-900 mb-1">Need Help?</h4>
              <p className="text-sm text-jet-blue-800 mb-2">
                If you continue to experience payment issues, our billing support team is here to help.
              </p>
              <div className="text-sm text-jet-blue-800">
                <p>📧 billing-support@jetscanner360.com</p>
                <p>📞 +1 (800) BILLING (245-5464)</p>
                <p>💬 Live chat available 24/7</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="text-center space-y-3 pt-4 border-t border-jet-gray-200">
          <button
            onClick={() => navigate('/admin/auth/login')}
            className="text-jet-red-600 hover:text-jet-red-700 text-sm transition-colors"
          >
            Back to Admin Login
          </button>
          
          <div className="text-sm text-jet-gray-600">
            <button
              onClick={() => navigate('/admin/billing/history')}
              className="text-jet-blue-600 hover:text-jet-blue-700 font-medium"
            >
              View Billing History
            </button>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-jet-gray-50 border border-jet-gray-200 rounded-md p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-jet-gray-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
            </svg>
            <div>
              <h4 className="text-sm font-medium text-jet-gray-900 mb-1">Secure Payment Processing</h4>
              <p className="text-sm text-jet-gray-700">
                All payment information is processed securely using industry-standard encryption. 
                Your financial data is never stored on our servers.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default AdminPaymentFailurePage;